class ProductQuickAdd extends HTMLElement {
  connectedCallback() {
    this.productJson         = JSON.parse(this.getAttribute('data-product-json'));
    this.productHandle       = this.getAttribute('data-product-handle');
    this.variantOrderDefault = (this.getAttribute('data-variant-order-default') || '')
      .split(',').map(s => s.trim().toUpperCase()).filter(Boolean);

    this.cardInner      = this.closest('.card__inner');
    this.optionsWrapper = this.querySelector('.quick-add__options_wrapper');
    this.cart           = document.querySelector('cart-drawer');
    this.openButton     = this.cardInner?.querySelector('.product-quick-add__btn');

    this.productOptions = (this.productJson.options || []).map((opt, i) => ({
      name: opt.toLowerCase(),
      position: i + 1
    }));

    this.currentColor = this._getActiveSwatchColor();

    const swatchEl = this.closest('.card-wrapper')?.querySelector('product-card-color-swatch');
    if (swatchEl) {
      swatchEl.addEventListener('click', (e) => {
        const item = e.target.closest('[data-color]');
        if (item) {
          this.currentColor = item.getAttribute('data-color');
          this._forceClose();
        }
      });
    }

    this._outsideClickHandler = (e) => {
      if (!this.closest('.card-wrapper')?.contains(e.target)) {
        this._forceClose();
      }
    };

    if (this.openButton) {
      this.openButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.open();
      });

      this.openButton.addEventListener('touchend', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.open();
      }, { passive: false });
    }
  }

  _getActiveSwatchColor() {
    const active = this.closest('.card-wrapper')?.querySelector('[data-selected="true"]');
    return active ? active.getAttribute('data-color') : null;
  }

  _slugify(str) {
    return (str || '').toString().toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '-').replace(/[^\w-]/g, '');
  }

  open() {
    if (!this.cardInner) return;

    document.querySelectorAll('.card__inner.quick-add-open').forEach(el => {
      if (el !== this.cardInner) {
        el.classList.remove('quick-add-open');
        el.querySelector('.quick-add__options_wrapper')?.innerHTML &&
          setTimeout(() => { el.querySelector('.quick-add__options_wrapper').innerHTML = ''; }, 300);
      }
    });

    if (this.cardInner.classList.contains('quick-add-open')) {
      this._forceClose();
      return;
    }

    this.cardInner.classList.add('quick-add-open');
    document.addEventListener('click', this._outsideClickHandler);
    document.addEventListener('touchstart', this._outsideClickHandler, { passive: true });

    if (!this._buildVariantSelector()) {
      this._handleAddProduct();
    }
  }

  _forceClose() {
    if (!this.cardInner) return;
    this.cardInner.classList.remove('quick-add-open');
    document.removeEventListener('click', this._outsideClickHandler);
    document.removeEventListener('touchstart', this._outsideClickHandler);
    setTimeout(() => { this.optionsWrapper.innerHTML = ''; }, 300);
    if (this.openButton) {
      this.openButton.classList.remove('is-loading', 'is-added');
      this.openButton.textContent = 'COMPRA RÁPIDA';
    }
  }

  _buildVariantSelector() {
    const variants = this._getVariantsByColor();
    if (!variants.length) return false;

    const sizeOption = this.productOptions.find(o =>
      o.name !== 'cor' && o.name !== 'color' && o.name !== 'colour'
    );
    if (!sizeOption) return false;

    const optionKey = `option${sizeOption.position}`;
    const seen = new Set();
    const values = [];
    variants.forEach(v => {
      if (!seen.has(v[optionKey])) {
        seen.add(v[optionKey]);
        values.push({ value: v[optionKey], available: v.available });
      }
    });

    const sorted = this._sortValues(values);

    const labelsHtml = sorted.map(({ value, available }) => {
      const cls = available ? '' : ' quick-add__option__value--unavailable';
      const inputId = `${this.id}--${this._slugify(value)}`;
      return `
        <label class="quick-add__option__value${cls}" for="${inputId}">
          <input
            type="radio"
            name="size-${this.id}"
            id="${inputId}"
            value="${value}"
            data-option-position="${sizeOption.position}"
            ${available ? '' : 'disabled'}>
          <span>${value}</span>
        </label>`;
    }).join('');

    this.optionsWrapper.innerHTML = `
      <div class="quick-add__option">
        <div class="quick-add__option__values">${labelsHtml}</div>
      </div>`;

    this.optionsWrapper.querySelectorAll('input[type="radio"]').forEach(input => {
      input.addEventListener('change', (e) => this._handleVariantSelect(e));
    });

    const availableInputs = [...this.optionsWrapper.querySelectorAll('input:not([disabled])')];
    if (availableInputs.length === 1) {
      availableInputs[0].checked = true;
      availableInputs[0].closest('label').classList.add('quick-add__option__value--selected');
      this._handleAddProduct();
      return true;
    }

    return true;
  }

  _sortValues(values) {
    if (!this.variantOrderDefault.length) return values;
    const ordered = [], rest = [];
    this.variantOrderDefault.forEach(target => {
      const match = values.find(v => v.value.toUpperCase() === target);
      if (match && !ordered.includes(match)) ordered.push(match);
    });
    values.forEach(v => { if (!ordered.includes(v)) rest.push(v); });
    return [...ordered, ...rest];
  }

  _getVariantsByColor() {
    const colorOption = this.productOptions.find(o =>
      o.name === 'cor' || o.name === 'color' || o.name === 'colour'
    );
    if (!colorOption || !this.currentColor) return this.productJson.variants;
    const optionKey = `option${colorOption.position}`;
    return this.productJson.variants.filter(v =>
      this._slugify(v[optionKey]) === this._slugify(this.currentColor)
    );
  }

  _getSelectedVariant() {
    const sizeInput = this.optionsWrapper.querySelector('input[type="radio"]:checked');
    const variants  = this._getVariantsByColor();
    if (!sizeInput) return variants.find(v => v.available) || null;

    const sizeOption = this.productOptions.find(o =>
      o.name !== 'cor' && o.name !== 'color' && o.name !== 'colour'
    );
    if (!sizeOption) return null;
    const optionKey = `option${sizeOption.position}`;
    return variants.find(v => v[optionKey] === sizeInput.value && v.available) || null;
  }

  _handleVariantSelect(e) {
    const position = e.target.getAttribute('data-option-position');
    this.optionsWrapper.querySelectorAll(`input[data-option-position="${position}"]`).forEach(inp => {
      const label = inp.closest('label');
      if (inp === e.target) {
        label.classList.add('quick-add__option__value--selected');
      } else {
        inp.checked = false;
        label.classList.remove('quick-add__option__value--selected');
      }
    });
    this._handleAddProduct();
  }

  _handleAddProduct() {
    const variant = this._getSelectedVariant();
    if (!variant) return;
    this._addToCart(variant);
  }

  _addToCart(variant) {
    if (!this.openButton) return;
    if (this.openButton.classList.contains('is-loading')) return;

    this.openButton.classList.add('is-loading');
    this.openButton.textContent = 'ADICIONANDO...';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: variant.id, quantity: 1 }],
        sections: ['cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname
      })
    })
    .then(r => {
      if (!r.ok) return r.json().then(err => { throw err; });
      return r.json();
    })
    .then(data => {
      this.openButton.classList.remove('is-loading');
      this.openButton.classList.add('is-added');
      this.openButton.textContent = '✓ ADICIONADO';

      if (this.cart) {
        this.cart.classList.remove('is-empty');
        const drawerInner = this.cart.querySelector('.drawer__inner');
        if (drawerInner) drawerInner.classList.remove('is-empty');

        if (typeof this.cart.renderContents === 'function') {
          this.cart.renderContents({
            id: variant.id,
            sections: data.sections
          });
        }
      }

      document.dispatchEvent(new CustomEvent('cart:updated')); // ← atualiza barra de desconto progressivo
      setTimeout(() => this._forceClose(), 900);
    })
    .catch(err => {
      console.error('[QuickAdd] Erro:', err);
      this.openButton.classList.remove('is-loading');
      this.openButton.textContent = 'COMPRA RÁPIDA';
    });
  }
}

if (!customElements.get('product-quick-add')) {
  customElements.define('product-quick-add', ProductQuickAdd);
}
